import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  AuthenticationProvider as DefaultProvider,
  oidcLog,
  getUserManager,
  useReactOidc,
  AuthenticationContext,
} from "@axa-fr/react-oidc-context";
import { Authenticating } from "components/login/Authenticating";
import { CallbackPage } from "components/login/CallbackPage";
import { NotAuthenticated } from "components/login/NotAuthenticated";
import { SessionExpired } from "components/login/SessionExpired";
import { Forbidden } from "@totalsoft/rocket-webapp-components-deprecated";
import { env } from "utils/env";
import { getTenantId, getTenant } from "providers/TenantProvider";
import { setUserManager } from "@axa-fr/react-oidc-core/dist/services/authenticationService";
import { AppContext } from "./AppContextProvider";
import i18next from "i18next";

const root = `${window.location.protocol}//${window.location.hostname}${
  window.location.port ? `:${window.location.port}` : ""
}`;

export const AUTH = {
  CALLBACK: "/authentication/callback",
  SILENT_CALLBACK: "/authentication/silent_callback",
  LOGOUT_REDIRECT: "/authentication/logout",
  SESSION_LOST: "/authentication/session-lost",
};

const getAuthenticationConfiguration = (appContext) => {
  const { countryCode, vendorCode } = getTenant();
  return {
    acr_values: `tenant:${getTenantId()}`,
    ui_locales: appContext?.language || i18next.language,
    client_id: env.REACT_APP_IDENTITY_CLIENT_ID,
    authority: env.REACT_APP_IDENTITY_AUTHORITY,
    redirect_uri: `${root}${`/${countryCode}/${vendorCode}`}${AUTH.CALLBACK}`,
    silent_redirect_uri: `${root}${`/${countryCode}/${vendorCode}`}${AUTH.SILENT_CALLBACK}`,
    response_type: "code",
    scope: "openid profile " + env.REACT_APP_IDENTITY_SCOPE,
    post_logout_redirect_uri: `${root}${`/${countryCode}/${vendorCode}`}${AUTH.LOGOUT_REDIRECT}`,
    automaticSilentRenew: true,
    loadUserInfo: true,
    triggerAuthFlow: true,
    revokeAccessTokenOnSignout: true,
    register_uri: `${env.REACT_APP_IDENTITY_AUTHORITY}/${env.REACT_APP_REGISTER_REDIRECT_URL}?returnUrl=${root}/register&clientId=${env.REACT_APP_IDENTITY_CLIENT_ID}`,
  };
};

const userSignedOut = (callback) => async () => {
  const userManager = getUserManager();
  await userManager.removeUser();
  callback();
};

const TenantReader = (props) => {
  const { children } = props;
  const setTenant = useContext(TenantContext);
  const { events } = useReactOidc();

  useEffect(() => {
    if (!events) return;

    const onUserSignedOut = userSignedOut(setTenant);
    events.addUserSignedOut(onUserSignedOut);

    return () => {
      events.removeUserSignedOut(onUserSignedOut);
    };
  }, [events, setTenant]);

  return children;
};

TenantReader.propTypes = { children: PropTypes.element.isRequired };

export const TenantContext = createContext();

const StubAuthenticationProvider = (props) => {
  const { children } = props;
  const stubOidcUser = useMemo(() => ({ profile: { name: "User" }, expired: false, isEnabled: false }), []);

  const stubEvents = useMemo(() => ({ addUserSignedOut: () => {}, removeUserSignedOut: () => {} }), []);

  const stubOidcContext = useMemo(
    () => ({
      oidcUser: stubOidcUser,
      isAuthenticated: true,
      isLoading: false,
      logout: () => {},
      signinSilent: async () => stubOidcUser,
      events: stubEvents,
    }),
    [stubOidcUser, stubEvents]
  );

  useEffect(() => {
    // minimal userManager stub that does not expose tokens but satisfies getUserManager().getUser()
    const stubUserManager = {
      getUser: async () => stubOidcUser,
      signinSilent: async () => stubOidcUser,
      removeUser: async () => {},
    };

    try {
      setUserManager(stubUserManager);
    } catch (e) {
      /* eslint-disable-next-line no-console */
      console.debug("setUserManager stub failed", e);
    }

    return () => {
      try {
        setUserManager(null);
      } catch (e) {
        /* eslint-disable-next-line no-console */
        console.debug("restore setUserManager failed", e);
      }
    };
  }, [stubOidcUser]);

  return (
    <AuthenticationContext.Provider value={stubOidcContext}>
      <TenantReader>{children}</TenantReader>
    </AuthenticationContext.Provider>
  );
};

StubAuthenticationProvider.propTypes = { children: PropTypes.element.isRequired };

const AuthenticationProvider = (props) => {
  const { children } = props;
  const { appContext } = useContext(AppContext);
  const [tenant, setTenant] = useState();

  const setTenantCallback = useCallback(
    async (e) => {
      if ((!e && !tenant) || `${e}`.toUpperCase() === `${tenant}`.toUpperCase()) return;

      const userManager = getUserManager();
      await userManager.removeUser();
      setUserManager(null);
      setTenant(e);
    },
    [setTenant, tenant]
  );
  const configuration = useMemo(() => getAuthenticationConfiguration(appContext), [appContext]);

  return (
    <TenantContext.Provider value={setTenantCallback}>
      {env.REACT_APP_USE_CIAM?.toLowerCase() === "true" ? (
        <StubAuthenticationProvider>{children}</StubAuthenticationProvider>
      ) : (
        <DefaultProvider
          configuration={configuration}
          loggerLevel={oidcLog.INFO}
          isEnabled={true}
          authenticating={Authenticating}
          callbackComponentOverride={CallbackPage}
          notAuthenticated={NotAuthenticated}
          sessionLostComponent={SessionExpired}
          notAuthorized={Forbidden}
        >
          <TenantReader>{children}</TenantReader>
        </DefaultProvider>
      )}
    </TenantContext.Provider>
  );
};

AuthenticationProvider.propTypes = { children: PropTypes.element.isRequired };

export default AuthenticationProvider;
